<script setup lang="ts">
  defineOptions({
    name: 'CPPortalSidebarNav',
  })

  import type { PropType } from 'vue'
  import { computed, h, resolveComponent } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'
  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
  import { useCoursesAdminPreview } from '@courses/admin-preview-layer/composables/useCoursesAdminPreview'

  import AppSidebarGroup from '@common/shared-ui-layer/components/AppSidebar/AppSidebarGroup.vue'
  import SelectableListRender from '@common/shared-ui-layer/components/SelectableListRender.vue'

  import CPTooltip from '@ui/components/CPTooltip.vue'
  import CPText from '@ui/components/CPText.vue'
  import type { ItemRendererParams } from '@common/shared-ui-layer/types/selectable-list'

  import CPPortalSidebarIcon from './CPPortalSidebarIcon.vue'

  import type { PortalNavigationGroup, PortalNavigationItem } from '../types/portal-sidebar'

  const props = defineProps({
    navigationGroups: {
      type: Array as PropType<PortalNavigationGroup[]>,
      required: true,
    },
    selectedItem: {
      type: Object as PropType<PortalNavigationItem | undefined>,
      default: undefined,
    },
    isCollapsed: {
      type: Boolean,
      required: true,
    },
  })

  const emit = defineEmits<{
    itemClick: [item: PortalNavigationItem]
  }>()

  const { t } = useI18n()

  const { getRoutePath } = useCPNavigation()

  // Gated here too (not just CPPortalSidebar) since this nav also renders inside
  // CPPortalSidebarMobileDrawer, which wouldn't inherit the desktop shell's guard.
  const { isActive, builderMode } = useCoursesAdminPreview()
  const isAdminPreviewBuilder = computed(() => Boolean(isActive.value && builderMode.value))

  const NuxtLink = resolveComponent('NuxtLink')

  /** Band must not render a section header (or empty list) when there are no child nav items. */
  const visibleNavigationGroups = computed(() => props.navigationGroups.filter(group => group.items.length > 0))

  const groupHeaderText = (group: PortalNavigationGroup) => {
    if (!group.labelKey) {
      return ''
    }
    return t(group.labelKey)
  }

  const navigationItemRenderer = ({ item, isActive }: ItemRendererParams<PortalNavigationItem>) => {
    const linkClass = [
      'cp-portal-nav-item__link',
      'flex h-8 w-full min-w-0 cursor-pointer items-center gap-2 p-2 overflow-hidden text-nowrap',
      'text-inherit no-underline visited:no-underline hover:no-underline outline-none transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary justify-start',
    ].join(' ')

    const iconWrap = h(CPPortalSidebarIcon, {
      channel: { iconName: item.iconName, fallbackIcon: item.fallbackIcon },
      isActive,
      class: 'h-4 w-4 flex-shrink-0 text-inherit',
    })

    const labelBlock = h(
      CPText,
      {
        size: 'lg',
        weight: 'medium',
        htmlTag: 'span',
        class: 'cp-portal-nav-item__label min-w-0 truncate',
      },
      { default: () => item.name }
    )

    const linkInner = () =>
      h(
        NuxtLink,
        {
          to: getRoutePath({ name: item.routeName }),
          class: linkClass,
          id: createElementId('cp-portal-sidebar', 'nav', item.id),
          'aria-label': item.name,
        },
        () => [iconWrap, labelBlock]
      )

    return h(
      CPTooltip,
      {
        placement: 'right',
        variant: 'dark',
        disabled: !props.isCollapsed,
      },
      {
        trigger: linkInner,
        default: () => item.name,
      }
    )
  }
</script>

<template>
  <div
    class="flex w-full flex-col group gap-2 lg:gap-4 data-[collapsible=icon]:gap-1"
    :class="{ 'pointer-events-none select-none': isAdminPreviewBuilder }"
    :aria-disabled="isAdminPreviewBuilder"
    :tabindex="isAdminPreviewBuilder ? -1 : undefined"
    :data-collapsible="isCollapsed ? 'icon' : 'content'"
  >
    <AppSidebarGroup
      v-for="group in visibleNavigationGroups"
      :key="group.groupId"
      :header="groupHeaderText(group)"
      header-class="mb-0 pl-[10px] outline-hidden transition-[height,margin,padding,opacity] duration-200 ease-linear group-data-[collapsible=icon]:static group-data-[collapsible=icon]:z-0 group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:h-0 group-data-[collapsible=icon]:min-h-0 group-data-[collapsible=icon]:overflow-hidden group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:opacity-0"
    >
      <SelectableListRender
        :items="group.items"
        :active-value="selectedItem?.id"
        :item-renderer="navigationItemRenderer"
        active-background-color="bg-primary"
        active-border-color="border-primary"
        :active-text-color="'text-on-primary'"
        hover-background-color="hover:bg-sidebar-hover"
        item-wrapper-class="rounded-md"
        :loading-text="t('common.loading')"
        @item-click="emit('itemClick', $event)"
      />
    </AppSidebarGroup>
  </div>
</template>
